import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from '../auth-client.service';
import { Actions, ofType, createEffect, concatLatestFrom } from '@ngrx/effects';
import {
  map,
  tap,
  switchMap,
  catchError,
  withLatestFrom
} from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { of } from 'rxjs';
import * as AuthActions from './auth.actions';
import * as AccountActions from '../../+store/account/account.actions';
import { UserService } from '../../services/api/user.service-client';
import { Store } from '@ngrx/store';
import { AppState } from '../../+store/app.states';
import {
  PATIENT_WEB_PAGES_URLS,
  notificationMessages
} from '@care-portals/data';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { PresenceService } from '../../services/presence.service';
import { getRedirectUrl } from './auth.selectors';
import * as CheckoutActions from '../../+store/checkout-v2/checkout.actions';
import * as CheckoutSelectors from '../../+store/checkout-v2/checkout.selectors';

@Injectable()
export class AuthEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly notification: NzNotificationService,
    private readonly store: Store<AppState>,
    private readonly gtm: Angulartics2GoogleTagManager,
    private readonly presenceService: PresenceService
  ) {}

  // return fbUser.pipe(
  //   tap(fbUser => console.log('authState user=', fbUser)),
  //   )

  validateAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.validateAuth),
      switchMap(() => {
        const token = this.authService.getToken();
        if (!token) {
          return of(AuthActions.unauthenticatedState());
        } else {
          return of(AuthActions.loadUser({ token }));
        }
      }),
      catchError(error => of(AuthActions.logInFailure(error)))
    )
  );

  unauthenticatedState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.unauthenticatedState),
        tap(() => {
          const urlSearchParams = new URLSearchParams(window.location.search);
          const params = Object.fromEntries((urlSearchParams as any).entries());
          if (params?.referrer || params?._ref) {
            // keeping 'referrer' for backwards compatibility
            this.authService.saveReferrer(params?.referrer || params?._ref);
          }
        })
      ),
    { dispatch: false }
  );

  signUp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.signUp),
      map(({ newCustomerDTO }) => ({
        ...newCustomerDTO,
        referrer: this.authService.getReferrer()
      })),
      switchMap(newCustomerDTO =>
        this.userService.create(newCustomerDTO).pipe(
          map(customer =>
            AuthActions.signUpSuccess({ newCustomerDTO: customer })
          ),
          catchError(error => of(AuthActions.signUpFailure(error)))
        )
      )
    )
  );

  signUpSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.signUpSuccess),
      tap(({ newCustomerDTO }) => {
        this.gtm.eventTrack('signUp', {
          category: 'accounts',
          label: 'signUp',
          gtmCustom: {
            email: newCustomerDTO.email
          }
        });
      }),
      map(res =>
        AuthActions.logIn({
          username: res.newCustomerDTO.email,
          password: res.newCustomerDTO.password
        })
      )
    )
  );

  signUpFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.signUpFailure),
        tap(({ error }) => {
          this.notification.error(
            notificationMessages?.UserCreationFailed?.title,
            error?.message || notificationMessages?.UserCreationFailed?.message
          );
        })
      ),
    { dispatch: false }
  );

  logIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logIn),
      switchMap(params =>
        this.authService
          .login({
            username: params.username,
            password: params.password
          })
          .pipe(
            map(res =>
              AuthActions.logInSuccess({
                token: res.token,
                email: params.username
              })
            ),
            catchError(error => {
              return of(AuthActions.logInFailure({
                error: error?.error,
                username: params.username
              }));
            })
          )
      )
    )
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logInSuccess),
      withLatestFrom(this.store.select(CheckoutSelectors.selectCart)),
      tap(([{ token }]) => this.authService.saveToken(token)),
      tap(([{ email }]) => {
        this.gtm.eventTrack('login', {
          category: 'accounts',
          label: 'login',
          gtmCustom: {
            email: email
          }
        });
      }),
      switchMap(([{ token }, cart]) => {
        const actions: any[] = [AuthActions.loadUser({ token })];
        if (cart) {
          actions.push(CheckoutActions.reloadCart());
        }

        return actions;
      })
    )
  );

  logInFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logInFailure),

        tap(({ error, username }) => {
          if (error.message === 'verificationRequired') {
            this.router.navigate([PATIENT_WEB_PAGES_URLS.auth.verifyEmail], {
              queryParams: { email: username }
            });
            this.notification.warning(
              notificationMessages.verifyEmailRequired.title,
              notificationMessages.verifyEmailRequired.message,
              { nzDuration: 5000, nzPlacement: 'top' }
            );
          } else {
            this.notification.error(
              notificationMessages.LoginFailed.title,
              notificationMessages.LoginFailed.message,
              { nzDuration: 5000, nzPlacement: 'top' }
            );
          }
        })
      ),
    { dispatch: false }
  );

  // called on success of auth
  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadUser),
      switchMap(() =>
        this.userService.getCustomerInfo().pipe(
          map(payload => AuthActions.loadUserSuccess({ payload })),
          catchError(error => of(AuthActions.loadUserFailure({ error })))
        )
      )
    )
  );

  loadUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadUserSuccess),
      tap(({ payload }) => {
        // Analytics/presence are side concerns: a throw here would tear down
        // this effect before the switchMap below dispatches the navigation and
        // account loads, leaving the user stranded on the login screen.
        try {
          this.gtm.setUsername(payload._id);
          this.presenceService.startTracking(payload._id);
        } catch (err) {
          console.error('Post-login tracking failed', err);
        }
      }),
      switchMap(({ payload }) => [
        AuthActions.navigateToRedirectLink(),
        AccountActions.loadAccount(),
        AccountActions.loadPaymentMethods()
      ])
    )
  );

  loadUserFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadUserFailure),
      tap(() => {
        this.notification.error(
          notificationMessages.InvalidSessionSessionTimeout.title,
          notificationMessages.InvalidSessionSessionTimeout.message
        );
        this.authService.logout();
        this.router.navigateByUrl('/' + PATIENT_WEB_PAGES_URLS.auth.login);
      }),
      map(() => {
        return AuthActions.unauthenticatedState();
      })
    )
  );

  logOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logOut),
      tap(() => {
        this.authService.logout();
        this.presenceService.stopTracking();
      }),
      map(res => AuthActions.logOutSuccess())
    )
  );

  logOutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logOutSuccess),
        tap(() =>
          this.router.navigateByUrl('/' + PATIENT_WEB_PAGES_URLS.auth.login)
        )
      ),
    { dispatch: false }
  );

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forgotPassword),
      switchMap(({ username }) =>
        this.userService.forgotPassword(username).pipe(
          map(res => AuthActions.forgotPasswordSuccess(res)),
          catchError(e =>
            of(AuthActions.forgotPasswordFailure({ error: e.error }))
          )
        )
      )
    )
  );

  forgotPasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.forgotPasswordSuccess),
        map(res =>
          this.notification.success(
            notificationMessages.Success_ForgotPassword.title,
            notificationMessages.Success_ForgotPassword.message
          )
        )
      ),
    { dispatch: false }
  );

  forgotPasswordFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.forgotPasswordFailure),
        map(params => params.error),
        tap(error =>
          this.notification.error(
            notificationMessages.GenericFailure_Short.title,
            notificationMessages.GenericFailure_Short.message +
              ': ' +
              error.message
          )
        )
      ),
    { dispatch: false }
  );

  validPasswordToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.validPasswordToken),
      switchMap(({ token }) =>
        this.userService.validPasswordToken(token).pipe(
          map(res => AuthActions.validPasswordTokenSuccess()),
          catchError(e =>
            of(AuthActions.validPasswordTokenFailure({ error: e.error }))
          )
        )
      )
    )
  );

  validPasswordTokenSuccess$ = createEffect(
    () => this.actions$.pipe(ofType(AuthActions.validPasswordTokenSuccess)),
    { dispatch: false }
  );

  validPasswordTokenFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.validPasswordTokenFailure),
        map(params => params.error),
        tap(error => {
          this.notification.error(
            notificationMessages.GenericFailure_Short.title,
            notificationMessages.GenericFailure_Short.message
          );
          this.router.navigateByUrl('/auth/forgot');
        })
      ),
    { dispatch: false }
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      switchMap(({ token, password }) =>
        this.userService.resetPassword(token, password).pipe(
          map(res =>
            AuthActions.resetPasswordSuccess({
              username: res.data.username,
              password
            })
          ),
          catchError(e =>
            of(AuthActions.resetPasswordFailure({ error: e.error }))
          )
        )
      )
    )
  );

  resetPasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPasswordSuccess),
      tap(res => {
        this.notification.success(
          notificationMessages.Success_PasswordSaved.title,
          notificationMessages.Success_PasswordSaved.message
        );
      }),
      map(res => {
        return AuthActions.logIn({ ...res });
      })
    )
  );

  resetPasswordFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.resetPasswordFailure),
        tap(({ error }) => {
          this.notification.error(
            notificationMessages.GenericFailure_Short.title,
            notificationMessages.GenericFailure_Short.message +
              ': ' +
              error.message
          );
        })
      ),
    { dispatch: false }
  );

  navigateToRedirectLink$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.navigateToRedirectLink),
        concatLatestFrom(action => this.store.select(getRedirectUrl)),
        map(([action, redirectUrl]) => {
          if (redirectUrl?.startsWith('http')) {
            window.location.href = redirectUrl;
          } else {
            this.router.navigateByUrl(
              redirectUrl || PATIENT_WEB_PAGES_URLS.account.root
            );
          }
        })
      ),
    { dispatch: false }
  );

  loginWithVerifyLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginWithVerifyLink),
      switchMap(({ verifyToken }) =>
        this.authService.loginWithVerifyLink(verifyToken).pipe(
          map(res =>
            AuthActions.loginWithVerifyLinkSuccess({ accessToken: res })
          ),
          catchError(e =>
            of(AuthActions.loginWithVerifyLinkFailure({ error: e.error }))
          )
        )
      )
    )
  );

  loginWithVerifyLinkSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginWithVerifyLinkSuccess),
      tap(() => {
        this.notification.success(
          notificationMessages.verifyEmailSuccess.title,
          notificationMessages.verifyEmailSuccess.message
        );
      }),
      map(({ accessToken }) => {
        return AuthActions.logInSuccess({ token: accessToken.token });
      })
    )
  );

  loginWithVerifyLinkFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginWithVerifyLinkFailure),
        tap(({ error }) => {
          this.notification.error(
            notificationMessages.GenericFailure_Short.title,
            notificationMessages.GenericFailure_Short.message +
              ': ' +
              error.message
          );
        })
      ),
    { dispatch: false }
  );
}
